import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { FRONTEND_BUILD, FRONTEND_VERSION } from '@/config/version';
import { t } from '@/i18n';

const CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * An SPA keeps running the bundle it loaded until the tab reloads — a deploy
 * alone changes nothing in open tabs. This notifier re-fetches /config.js
 * (rewritten by the container entrypoint on every deploy, fetched with
 * no-store so nginx/browser caches are bypassed) every 5 minutes and on tab
 * focus; a version different from the one baked into this bundle raises one
 * persistent toast with a reload action. Local dev ('dev' version) is exempt.
 */
export function VersionUpdateNotifier() {
  const notified = useRef(false);

  useEffect(() => {
    if (FRONTEND_VERSION === 'dev') return;

    const check = async () => {
      if (notified.current) return;
      try {
        // The Date.now() buster gives every check a unique cache key — it
        // punches through edge caches (Cloudflare) that no-store alone
        // cannot bypass.
        const res = await fetch(`/config.js?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const text = await res.text();
        const version = text.match(/VITE_APP_VERSION['"]?\s*:\s*['"]([^'"]+)['"]/)?.[1];
        const build = text.match(/VITE_BUILD_HASH['"]?\s*:\s*['"]([^'"]+)['"]/)?.[1];
        if (!version || version.startsWith('__')) return;
        // The build hash changes on EVERY deploy — it catches a redeploy even
        // when the human-facing version was not bumped.
        const changed = version !== FRONTEND_VERSION
          || (!!build && !build.startsWith('__') && build !== FRONTEND_BUILD);
        if (changed) {
          const liveVersion = version;
          notified.current = true;
          toast.info(t('common.newVersionAvailable', { version: liveVersion }), {
            duration: Infinity,
            action: {
              label: t('common.refreshNow'),
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        // Offline / transient — the next tick will try again.
      }
    };

    const timer = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    check();
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return null;
}
